import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import "./InAppBrowserGate.css";

type MobileOs = "android" | "ios" | "other";
type InAppApp =
  | "kakaotalk"
  | "naver"
  | "line"
  | "instagram"
  | "facebook"
  | "twitter"
  | "daum"
  | "unknown";

interface InAppBrowserInfo {
  isInApp: boolean;
  os: MobileOs;
  app: InAppApp;
}

const AUTO_OPEN_ONCE_KEY = "career_counseling_iab_auto_open_once";

function detectInAppBrowser(userAgent: string): InAppBrowserInfo {
  const ua = userAgent.toLowerCase();
  const isAndroid = /android/.test(ua);
  const isIOS = /iphone|ipad|ipod/.test(ua);

  let app: InAppApp = "unknown";
  if (ua.includes("kakaotalk")) {
    app = "kakaotalk";
  } else if (ua.includes("naver")) {
    app = "naver";
  } else if (ua.includes("line/")) {
    app = "line";
  } else if (ua.includes("instagram")) {
    app = "instagram";
  } else if (ua.includes("fban") || ua.includes("fbav") || ua.includes("fb_iab")) {
    app = "facebook";
  } else if (ua.includes("twitter")) {
    app = "twitter";
  } else if (ua.includes("daumapps")) {
    app = "daum";
  }

  const isAndroidWebView =
    isAndroid && (ua.includes("; wv)") || ua.includes("; wv;") || /\bwv\b/.test(ua));
  const isIOSWebView = isIOS && ua.includes("applewebkit") && !ua.includes("safari");
  const isInApp = app !== "unknown" || isAndroidWebView || isIOSWebView;
  const os: MobileOs = isAndroid ? "android" : isIOS ? "ios" : "other";

  return { isInApp, os, app };
}

function buildExternalOpenUrl(targetUrl: string, info: InAppBrowserInfo): string | null {
  if (info.app === "kakaotalk") {
    return `kakaotalk://web/openExternal?url=${encodeURIComponent(targetUrl)}`;
  }

  if (info.os !== "android") {
    return null;
  }

  try {
    const parsed = new URL(targetUrl);
    const scheme = parsed.protocol.replace(":", "");
    const path = `${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
    return `intent://${path}#Intent;scheme=${scheme};package=com.android.chrome;end`;
  } catch {
    return null;
  }
}

function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value);
  }

  return new Promise((resolve, reject) => {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);

    if (copied) {
      resolve();
      return;
    }
    reject(new Error("Failed to copy URL."));
  });
}

interface InAppBrowserGateProps {
  children: ReactNode;
}

const InAppBrowserGate = ({ children }: InAppBrowserGateProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const info = useMemo(() => detectInAppBrowser(navigator.userAgent), []);

  const tryOpenInExternalBrowser = useCallback(() => {
    const targetUrl = window.location.href;
    const externalUrl = buildExternalOpenUrl(targetUrl, info);

    if (externalUrl) {
      window.location.href = externalUrl;
      return;
    }

    window.open(targetUrl, "_blank", "noopener,noreferrer");
  }, [info]);

  useEffect(() => {
    if (!info.isInApp) {
      return;
    }

    const shouldAutoTry = info.os === "android" || info.app === "kakaotalk";
    if (!shouldAutoTry) {
      return;
    }

    if (sessionStorage.getItem(AUTO_OPEN_ONCE_KEY) === "1") {
      return;
    }

    sessionStorage.setItem(AUTO_OPEN_ONCE_KEY, "1");
    tryOpenInExternalBrowser();
  }, [info, tryOpenInExternalBrowser]);

  const handleCopy = useCallback(async () => {
    try {
      await copyText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, []);

  if (!info.isInApp) {
    return <>{children}</>;
  }

  return (
    <div className="inapp-gate-overlay" role="alertdialog" aria-modal="true">
      <div className="inapp-gate-panel">
        <h1 className="inapp-gate-title">{t("inAppBlockedTitle")}</h1>
        <p className="inapp-gate-message">{t("inAppBlockedMessage")}</p>
        <p className="inapp-gate-manual">
          {info.os === "ios"
            ? t("inAppBlockedManualIOS")
            : t("inAppBlockedManualAndroid")}
        </p>

        {copied && <p className="inapp-gate-copied">{t("inAppBlockedCopied")}</p>}

        <div className="inapp-gate-actions">
          <button
            type="button"
            className="inapp-gate-btn inapp-gate-btn-primary"
            onClick={tryOpenInExternalBrowser}
          >
            {t("inAppBlockedAction")}
          </button>
          <button
            type="button"
            className="inapp-gate-btn inapp-gate-btn-secondary"
            onClick={handleCopy}
          >
            {t("inAppBlockedCopy")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InAppBrowserGate;
